import type { DefaultAutomationTemplateMetadata } from '@reach/shared-types/automation-template';
import type { EmailAutoSave, EmailAutoSaveContent } from '@reach/shared-types/communication-groups';
import type { ApiResponse } from '../../../lib/api';
import { apiRequest } from '../../../lib/api-utils';
import { API_ENDPOINTS } from '../../../lib/constants';
import type {
  AutomationStatusesResponse,
  CheckActiveSyncJobsResponse,
  CreateAutomationTemplateParams,
  DuplicateDefaultsResponse,
  TemplateResponse,
  TemplatesResponse,
  TenantSelection,
  UpdateAutomationTemplateParams,
  ValidateTenantSegmentsResponse,
} from './templates.types';

export async function getAutomationTemplates(): Promise<TemplatesResponse> {
  const response = await apiRequest<ApiResponse<TemplatesResponse>>(
    API_ENDPOINTS.AUTOMATION_TEMPLATES.LIST,
  );
  return response.data;
}

export async function getAutomationTemplate(id: string): Promise<TemplateResponse> {
  const response = await apiRequest<ApiResponse<TemplateResponse>>(
    API_ENDPOINTS.AUTOMATION_TEMPLATES.GET(id),
  );
  return response.data;
}

export async function createAutomationTemplate(
  params: CreateAutomationTemplateParams,
): Promise<TemplateResponse> {
  const response = await apiRequest<ApiResponse<TemplateResponse>>(
    API_ENDPOINTS.AUTOMATION_TEMPLATES.CREATE,
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
  );
  return response.data;
}

export async function updateAutomationTemplate(
  id: string,
  params: UpdateAutomationTemplateParams,
): Promise<TemplateResponse> {
  const response = await apiRequest<ApiResponse<TemplateResponse>>(
    API_ENDPOINTS.AUTOMATION_TEMPLATES.UPDATE(id),
    {
      method: 'PUT',
      body: JSON.stringify(params),
    },
  );
  return response.data;
}

export async function getTemplateEmailAutoSaves(
  templateId: string,
): Promise<Record<string, EmailAutoSave>> {
  const response = await apiRequest<ApiResponse<{ emailAutoSaves: Record<string, EmailAutoSave> }>>(
    API_ENDPOINTS.AUTOMATION_TEMPLATES.EMAIL_AUTO_SAVES(templateId),
  );
  return response.data.emailAutoSaves;
}

export async function saveTemplateEmailAutoSave(args: {
  templateId: string;
  templateActionId: string;
  content: EmailAutoSaveContent;
}): Promise<EmailAutoSave> {
  const { templateId, templateActionId, content } = args;
  const response = await apiRequest<ApiResponse<EmailAutoSave>>(
    API_ENDPOINTS.AUTOMATION_TEMPLATES.EMAIL_AUTO_SAVE(templateId, templateActionId),
    {
      method: 'PUT',
      body: JSON.stringify(content),
    },
  );
  return response.data;
}

export async function duplicateDefaultTemplates(): Promise<DuplicateDefaultsResponse> {
  const response = await apiRequest<ApiResponse<DuplicateDefaultsResponse>>(
    API_ENDPOINTS.AUTOMATION_TEMPLATES.DUPLICATE_DEFAULTS,
    {
      method: 'POST',
    },
  );
  return response.data;
}

export async function getDefaultAutomationMetadata(): Promise<{
  metadata: DefaultAutomationTemplateMetadata[];
}> {
  const response = await apiRequest<ApiResponse<{ metadata: DefaultAutomationTemplateMetadata[] }>>(
    API_ENDPOINTS.AUTOMATION_TEMPLATES.DEFAULT_METADATA,
  );
  return response.data;
}

export async function createPartnerCopyOfDefaultAutomation(
  templateName: string,
): Promise<{ success: boolean; created: boolean; templateId: string | null }> {
  const response = await apiRequest<
    ApiResponse<{
      success: boolean;
      created: boolean;
      templateId: string | null;
    }>
  >(API_ENDPOINTS.AUTOMATION_TEMPLATES.COPY_DEFAULT_AUTOMATION, {
    method: 'POST',
    body: JSON.stringify({ templateName }),
  });
  return response.data;
}

export async function createPartnerAutomationFromDefault(params: {
  defaultTemplateName: string;
  customName: string;
  customDescription: string;
}): Promise<{ created: boolean; templateId: string | null }> {
  const response = await apiRequest<
    ApiResponse<{
      created: boolean;
      templateId: string | null;
    }>
  >(API_ENDPOINTS.AUTOMATION_TEMPLATES.CREATE_FROM_DEFAULT, {
    method: 'POST',
    body: JSON.stringify(params),
  });
  return response.data;
}

export async function validateTenantSegments(
  templateId: string,
  selection: TenantSelection,
  segmentDefinitionIds?: string[],
): Promise<ValidateTenantSegmentsResponse> {
  const targeting =
    selection.selectionType === 'all'
      ? { targetedTenantsFilter: null, excludedTenantIds: selection.excludedTenantIds }
      : { targetedTenantsFilter: selection.selectedTenantIds };
  const body = segmentDefinitionIds ? { ...targeting, segmentDefinitionIds } : targeting;
  const response = await apiRequest<ApiResponse<ValidateTenantSegmentsResponse>>(
    API_ENDPOINTS.AUTOMATION_TEMPLATES.VALIDATE_TENANT_SEGMENTS(templateId),
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
  return response.data;
}

export async function syncAutomationTemplates(params?: {
  templateId?: string;
  businessIds?: string[];
}): Promise<void> {
  await apiRequest<ApiResponse<Record<string, never>>>(API_ENDPOINTS.AUTOMATION_TEMPLATES.SYNC, {
    method: 'POST',
    body: JSON.stringify(params ?? {}),
  });
}

export async function checkActiveSyncJobs(templateId: string): Promise<CheckActiveSyncJobsResponse> {
  const path = API_ENDPOINTS.AUTOMATION_TEMPLATES.SYNC_STATUS(templateId);
  const response = await apiRequest<ApiResponse<CheckActiveSyncJobsResponse>>(path);
  return response.data;
}

export async function getAutomationStatuses(templateId: string): Promise<AutomationStatusesResponse> {
  const response = await apiRequest<ApiResponse<AutomationStatusesResponse>>(
    API_ENDPOINTS.AUTOMATION_TEMPLATES.AUTOMATION_STATUSES(templateId),
  );
  return response.data;
}
